import React, { useState, useEffect, useRef, useMemo } from "react";
import HistoryItem from "../../elements/History/HistoryItem";
import "../../elements/History/history-item.css";

import { useTranslation, initReactI18next } from "react-i18next";
import i18n from "i18next";
import translationEN from "../../constants/locales/en/translation.json";
import translationID from "../../constants/locales/id/translation.json";

import {
  deleteRequest,
  createRequest,
  getAllRequest,
  pinRequest,
} from "./requests";
import SideToolBar from "./SideToolBar";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { filters, sorts, sortsByPinnedAt } from "./helpers";

const resources = {
  en: {
    translation: translationEN,
  },
  id: {
    translation: translationID,
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: localStorage.getItem("lang") || "en",
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

const HistoryPage = () => {
  const { t } = useTranslation();

  const modalRef = useRef();
  const [modal, setModal] = useState(false);

  useEffect(() => {
    if (!modalRef.current) return;
    if (!modalRef.current.showModal && !modalRef.current.close) return;
    if (modal) modalRef.current.showModal();
    else modalRef.current.close();
  }, [modal, modalRef]);

  // Typedef of items
  /**
   * @typedef {Object} Item
   * @property {string} content
   * @property {string} type
   * @property {string} created_at
   * @property {string} id
   * @property {string} updated_at
   * @property {string} pinned_at
   */

  const notifyError = (message) =>
    toast.error(message, { className: "toast-error" });


  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState(Object.keys(filters)[0]); // 'all'
  const [historySort, setHistorySort] = useState(Object.keys(sorts)[0]); // 'createdAtAsc'

  const historyPreprocessed = useMemo(() => {
    const historyPreprocessed = history
      .filter(filters[historyFilter].fn)
      .sort(sorts[historySort].fn)
      // Sort by pinned_at first
      .sort(sortsByPinnedAt);

    return historyPreprocessed;
  }, [history, historyFilter, historySort]);

  const firstRun = async () => {
    try {
      const response = await getAllRequest();
      // Login lagi error, jadi hardcode dulu
      console.log(response.data);
      setHistory(response.data);
    } catch (error) {
      if (error?.response?.data) {
        notifyError(error.response.data.detail);
      } else {
        notifyError(`Error getting documents: ${error.message}`);
      }
    }
  };
  // method: 'GET',
  useEffect(() => {
    firstRun();
  }, []);

  const newDocument = async () => {
    try {
      const response = await createRequest();
      // Get id from response
      const { id } = response.data;
      setHistory([...history, response.data]);
      window.location.href = `/product?id=${id}`;
    } catch (error) {
      if (error?.response?.data) {
        notifyError(error.response.data.detail);
      } else {
        notifyError(`Error creating new document: ${error.message}`);
      }
    }
  };

  const pinDocument = async (id) => {
    // Pin document
    try {
      const response = await pinRequest(id);
      // Update document in state
      // Find document by id
      const item = history.find((item) => "" + item.id === "" + id);
      // Update pinned_at
      item.pinned_at =
        response?.data?.pinned_at ??
        (item.pinned_at ? null : new Date().toISOString());
      console.log(item);
      // Update state
      setHistory([...history]);
    } catch (error) {
      if (error?.response?.data) {
        notifyError(error.response.data.detail);
      } else {
        notifyError(`Error pinning document: ${error.message}`);
      }
    }
  };

  const deleteDocument = async (id) => {
    try {
      await deleteRequest(id);
      // Delete document from state
      setHistory(history.filter((item) => "" + item.id !== "" + id));
    } catch (error) {
      if (error?.response?.data) {
        notifyError(error.response.data.detail);
      } else {
        notifyError(`Error deleting document: ${error.message}`);
      }
    }
  };

  /**
   *
   * @param {string} date - Date string
   * @returns
   */
  const formatDateTime = (date) => {
    return new Date(date).toLocaleDateString("en-GB");
  };

  return (
    <div className="flex h-screen">
      <ToastContainer position="bottom-center" />
      <aside>
        <SideToolBar />
      </aside>
      <main
        className="flex-grow h-screen overflow-hidden flex flex-col"
        style={{ backgroundColor: "#e7e7e7" }}
      >
        <h1 className="text-5xl mb-5 mt-16 ml-10" data-testid="history-doc">
          {t("history_doc")}
        </h1>
        <button
          id="history-filter-dialog"
          className="text-xl my-3 ml-10 border border-black w-fit px-5 py-2 rounded-full flex gap-2 items-center"
          data-testid="history-filter-sort-button"
          onClick={() => setModal(!modal)}
        >
          <svg
            className="h-6"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
          >
            <title>filter-menu</title>
            <path d="M11 11L16.76 3.62A1 1 0 0 0 16.59 2.22A1 1 0 0 0 16 2H2A1 1 0 0 0 1.38 2.22A1 1 0 0 0 1.21 3.62L7 11V16.87A1 1 0 0 0 7.29 17.7L9.29 19.7A1 1 0 0 0 10.7 19.7A1 1 0 0 0 11 18.87V11M13 16L18 21L23 16Z" />
          </svg>
          Filter and Sort
        </button>
        <div className="p-16 min-w-full overflow-auto">
          <main className="rounded grid grid-cols-4 gap-4">
            <button
              onClick={newDocument}
              className="history-item history-item-inverse"
            >
              <div className="text-center">
                <p className="text-7xl font-semibold">+</p>
                <p className="text-lg font-semibold" data-testid="history-add">
                  {t("history_add")}
                </p>
              </div>
            </button>
            {historyPreprocessed.map(
              ({ content, id, type, updated_at, pinned_at }, _) => (
                <HistoryItem
                  key={id}
                  src="https://via.placeholder.com/150"
                  title={content}
                  type={type}
                  id={id}
                  pinned={Boolean(pinned_at)}
                  date={formatDateTime(updated_at)}
                  onDelete={deleteDocument}
                  onPin={pinDocument}
                />
              )
            )}
          </main>
        </div>
        <dialog
          id="history-filter-dialog"
          data-testid="history-filter-sort-dialog"
          className="rounded-2xl p-5"
          ref={modalRef}
        >
          <main className="max-w-xl flex flex-col gap-4">
            <h1 className="text-2xl">{t("history_filter_sort_title")}</h1>
            <div>
              <h2>{t("history_filter_label")}</h2>
              {Object.entries(filters).map(([key, { name }]) => (
                <button
                  key={key}
                  onClick={() => setHistoryFilter(key)}
                  className={`${
                    key === historyFilter ? "bg-blue-500" : "bg-blue-300"
                  } p-1 m-1 rounded`}
                >
                  {t(name)}
                </button>
              ))}
            </div>
            <div>
              <h2>{t("history_sort_label")}</h2>
              {Object.entries(sorts).map(([key, { name }]) => (
                <button
                  key={key}
                  onClick={() => setHistorySort(key)}
                  className={`${
                    key === historySort ? "bg-blue-500" : "bg-blue-300"
                  } p-1 m-1 rounded`}
                >
                  {t(name)}
                </button>
              ))}
            </div>
            <button
              onClick={() => setModal(!modal)}
              className="bg-red-600 p-1 m-1 text-white hover:bg-red-400 mt-5 px-3 py-2 rounded-lg"
            >
              {t("close")}
            </button>
          </main>
        </dialog>
      </main>
      <aside></aside>
    </div>
  );
};

export default HistoryPage;
